import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchCustomerNotifications,
  markCustomerNotificationRead,
  markAllCustomerNotificationsRead,
  deleteCustomerNotification,
} from '../api/customerNotifications';
import {
  Bell,
  Package,
  Truck,
  CheckCircle,
  AlertTriangle,
  Tag,
  Gift,
  Star,
  Trash2,
  Check,
  CheckCheck,
  ChevronRight,
  ShoppingBag,
  Heart,
  Wallet,
  Megaphone
} from 'lucide-react';

interface Notification {
  _id: string;
  type: 'order' | 'promo' | 'system' | 'wishlist' | 'payment' | 'reward' | 'shipping' | 'contact';
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  actionUrl?: string;
  actionText?: string;
  image?: string;
}

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread' | 'orders' | 'promos'>('all');

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchCustomerNotifications({ limit: 50 });
      const mapped: Notification[] = (res.data || []).map((n) => ({
        _id: n._id,
        type: (n.type === 'shipping' ? 'order' : n.type === 'contact' ? 'system' : n.type) as Notification['type'],
        title: n.title,
        message: n.message,
        read: n.read,
        createdAt: n.createdAt,
        actionUrl: n.actionUrl,
      }));
      setNotifications(mapped);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (id: string) => {
    try {
      await markCustomerNotificationRead(id);
      setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, read: true } : n)));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await markAllCustomerNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await deleteCustomerNotification(id);
      setNotifications((prev) => prev.filter((n) => n._id !== id));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <Package className="w-5 h-5" />;
      case 'promo':
        return <Tag className="w-5 h-5" />;
      case 'system':
        return <AlertTriangle className="w-5 h-5" />;
      case 'wishlist':
        return <Heart className="w-5 h-5" />;
      case 'payment':
        return <Wallet className="w-5 h-5" />;
      case 'reward':
        return <Gift className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'order':
        return 'bg-blue-100 text-blue-600';
      case 'promo':
        return 'bg-purple-100 text-purple-600';
      case 'system':
        return 'bg-yellow-100 text-yellow-600';
      case 'wishlist':
        return 'bg-pink-100 text-pink-600';
      case 'payment':
        return 'bg-green-100 text-green-600';
      case 'reward':
        return 'bg-amber-100 text-amber-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'orders') return n.type === 'order';
    if (filter === 'promos') return n.type === 'promo';
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600">
            You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <CheckCheck className="w-5 h-5" />
            <span>Mark all as read</span>
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-xl shadow-sm mb-6">
        <div className="flex overflow-x-auto">
          {[
            { id: 'all', label: 'All', count: notifications.length },
            { id: 'unread', label: 'Unread', count: unreadCount },
            { id: 'orders', label: 'Orders', count: notifications.filter(n => n.type === 'order').length },
            { id: 'promos', label: 'Promotions', count: notifications.filter(n => n.type === 'promo').length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id as any)}
              className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                filter === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span>{tab.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                filter === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'unread' ? 'No unread notifications' : 'No notifications'}
            </h3>
            <p className="text-gray-600">
              {filter === 'unread' 
                ? 'You\'re all caught up!'
                : 'Check back later for updates on your orders and offers.'}
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification._id}
              className={`bg-white rounded-xl shadow-sm p-4 transition-all ${
                !notification.read ? 'border-l-4 border-blue-500' : ''
              }`}
            >
              <div className="flex items-start space-x-4">
                {/* Icon */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  getNotificationColor(notification.type)
                }`}>
                  {getNotificationIcon(notification.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-gray-900">
                          {notification.title}
                        </h3>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                        )}
                      </div>
                      <p className="text-gray-600 mt-1">{notification.message}</p>
                      <p className="text-sm text-gray-400 mt-2">
                        {getTimeAgo(notification.createdAt)}
                      </p>
                    </div>

                    {/* Image if present */}
                    {notification.image && (
                      <img
                        src={notification.image}
                        alt=""
                        className="w-16 h-16 rounded-lg object-cover flex-shrink-0 ml-4"
                      />
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-4 mt-3">
                    {notification.actionUrl && (
                      <a
                        href={notification.actionUrl}
                        onClick={() => markAsRead(notification._id)}
                        className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
                      >
                        {notification.actionText}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </a>
                    )}
                    {!notification.read && (
                      <button
                        onClick={() => markAsRead(notification._id)}
                        className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Mark as read
                      </button>
                    )}
                  </div>
                </div>

                {/* Delete Button */}
                <button
                  onClick={() => deleteNotification(notification._id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0"
                  title="Delete notification"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Notification Settings Link */}
      <div className="mt-8 text-center">
        <a
          href="/settings/notifications"
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <Megaphone className="w-5 h-5 mr-2" />
          Manage notification preferences
        </a>
      </div>
    </div>
  );
};

export default Notifications;
